import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Clock, Check, AlertCircle, Phone } from 'lucide-react';
import { contactFormData } from '../mock';
import axios from 'axios';
import SEO, { SEOPages } from '../components/SEO';
import { 
  validateContactForm, 
  formatInternationalPhone, 
  sanitizeInput 
} from '../utils/validation';
import { loadUserDataCache, saveUserDataCache } from '../utils/userDataCache';

import API_BASE_URL from '../config/api';
const API = API_BASE_URL;

// Load initial form data from shared cache
const getInitialFormData = () => {
  const cached = loadUserDataCache();
  return {
    nom: cached.nom || '',
    prenom: cached.prenom || '',
    email: cached.email || '',
    theme: '',
    creneau: '',
    hasConsultant: '',
    message: '',
    consent: false // RGPD consent - never cached
  };
};

const Contact = () => {
  const [formData, setFormData] = useState(getInitialFormData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState({}); // Champs que l'utilisateur a touchés

  // Save to shared cache when user data changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.nom || formData.prenom || formData.email) {
        saveUserDataCache({
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email
        });
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.nom, formData.prenom, formData.email]);

  // Validation en temps réel (seulement pour les champs touchés)
  useEffect(() => {
    const { isValid, errors } = validateContactForm(formData);
    
    // Filtrer les erreurs pour ne montrer que celles des champs touchés
    const filteredErrors = {};
    Object.keys(errors).forEach(field => {
      if (touchedFields[field]) {
        filteredErrors[field] = errors[field];
      }
    });
    
    setFieldErrors(filteredErrors);
    setIsFormValid(isValid);
  }, [formData, touchedFields]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs comme touchés lors de la soumission
    const allFields = ['nom', 'prenom', 'email', 'theme'];
    const allTouched = {};
    allFields.forEach(field => {
      allTouched[field] = true;
    });
    setTouchedFields(allTouched);
    
    // Vérification consentement RGPD
    if (!formData.consent) {
      setError('Veuillez accepter la politique de confidentialité pour continuer');
      return;
    }
    
    // Validation finale
    const { isValid, errors } = validateContactForm(formData);
    if (!isValid) {
      setFieldErrors(errors);
      setError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    // Nettoyage des données avant envoi
    const cleanedData = {
      nom: sanitizeInput(formData.nom),
      prenom: sanitizeInput(formData.prenom),
      email: formData.email.trim().toLowerCase(),
      theme: sanitizeInput(formData.theme),
      creneau: sanitizeInput(formData.creneau),
      hasConsultant: formData.hasConsultant,
      message: sanitizeInput(formData.message)
    };
    
    try {
      const response = await axios.post(`${API}/contact`, cleanedData);
      
      if (response.data.success) {
        setIsSubmitted(true);
      } else {
        setError('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('Erreur envoi formulaire:', err);
      
      // Gestion des erreurs de sécurité
      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Données invalides. Veuillez vérifier vos informations.');
      } else if (err.response?.status === 429) {
        setError('Trop de demandes. Veuillez réessayer dans quelques minutes.');
      } else {
        setError('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Marquer le champ comme touché si l'utilisateur a tapé quelque chose
    if (value.length > 0) {
      setTouchedFields({
        ...touchedFields,
        [name]: true
      });
    }
    
    // Effacer l'erreur du champ quand l'utilisateur tape (seulement si le champ était déjà touché)
    if (fieldErrors[name] && touchedFields[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: null
      });
    }
  };

  // Gestionnaire pour marquer un champ comme touché quand l'utilisateur quitte le champ
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields({
      ...touchedFields,
      [name]: true
    });
  };

  if (isSubmitted) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="contact-success-page">
        <SEO 
          title="Merci pour votre demande - ATOM Group SA" 
          description="Votre demande de conseil financier a été envoyée avec succès. Nous vous contacterons dans les plus brefs délais."
          canonical="/contact/success"
        />
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="glass p-12 rounded-3xl">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <Check size={40} className="text-white" />
              </div>
              <h1 className="text-4xl font-bold heading-display text-white mb-6" data-testid="success-title">
                Demande envoyée avec succès !
              </h1>
              <p className="text-xl text-gray-300 mb-8" data-testid="success-message">
                Merci pour votre demande. Nous vous contacterons dans les plus brefs délais pour programmer votre rendez-vous.
              </p>
              <p className="text-lg text-blue-300 mb-8" data-testid="email-confirmation">
                Un email de confirmation vous a été envoyé à l'adresse : <strong>{formData.email}</strong>
              </p>
              <div className="glass p-6 rounded-2xl">
                <h3 className="text-lg font-semibold text-white mb-4">Prochaines étapes :</h3>
                <ul className="text-left text-gray-300 space-y-2" data-testid="next-steps-list">
                  <li>• Confirmation par email dans les 2h</li>
                  <li>• Appel de notre équipe sous 24h</li>
                  <li>• Rendez-vous programmé selon vos disponibilités</li>
                  <li>• Première analyse gratuite et sans engagement</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <SEO {...SEOPages.contact} />
      {/* Hero */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold heading-display text-white mb-6">
              Parlons de vos projets
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Première analyse gratuite et sans engagement. Nos conseillers sont là pour vous accompagner.
            </p>
          </div>
        </div>
      </section>

      {/* Formulaire et informations */}
      <section className="pb-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Formulaire */}
            <div id="formulaire-contact" className="glass p-8 rounded-3xl">
              <h2 className="text-2xl font-bold heading-display text-white mb-8">
                Demander un conseil
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {error && (
                  <div className="glass p-4 rounded-xl border border-red-500/50 bg-red-500/10" data-testid="contact-error-message">
                    <div className="flex items-center">
                      <AlertCircle size={16} className="text-red-400 mr-2" />
                      <p className="text-red-300 text-sm">{error}</p>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      required
                      value={formData.nom}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 transition-all ${
                        fieldErrors.nom 
                          ? 'focus:ring-red-400 ring-1 ring-red-400/50' 
                          : 'focus:ring-blue-300'
                      }`}
                      placeholder="Votre nom"
                      data-testid="input-nom"
                    />
                    {fieldErrors.nom && (
                      <p className="text-red-400 text-xs mt-1 flex items-center">
                        <AlertCircle size={12} className="mr-1" />
                        {fieldErrors.nom}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      required
                      value={formData.prenom}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 transition-all ${
                        fieldErrors.prenom 
                          ? 'focus:ring-red-400 ring-1 ring-red-400/50' 
                          : 'focus:ring-blue-300'
                      }`}
                      placeholder="Votre prénom"
                      data-testid="input-prenom"
                    />
                    {fieldErrors.prenom && (
                      <p className="text-red-400 text-xs mt-1 flex items-center">
                        <AlertCircle size={12} className="mr-1" />
                        {fieldErrors.prenom}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 transition-all ${
                      fieldErrors.email 
                        ? 'focus:ring-red-400 ring-1 ring-red-400/50' 
                        : 'focus:ring-blue-300'
                    }`}
                    placeholder="votre@email.ch"
                    data-testid="input-email"
                  />
                  {fieldErrors.email && (
                    <p className="text-red-400 text-xs mt-1 flex items-center">
                      <AlertCircle size={12} className="mr-1" />
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Thème *
                  </label>
                  <select
                    name="theme"
                    required
                    value={formData.theme}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full p-3 glass border-0 rounded-xl text-white focus:ring-2 transition-all ${
                      fieldErrors.theme 
                        ? 'focus:ring-red-400 ring-1 ring-red-400/50' 
                        : 'focus:ring-blue-300'
                    }`}
                    data-testid="select-theme"
                  >
                    <option value="">Sélectionnez un thème</option>
                    {contactFormData.themes.map((theme) => (
                      <option key={theme} value={theme} className="bg-gray-800">
                        {theme}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.theme && (
                    <p className="text-red-400 text-xs mt-1 flex items-center">
                      <AlertCircle size={12} className="mr-1" />
                      {fieldErrors.theme}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Créneau souhaité
                  </label>
                  <select
                    name="creneau"
                    value={formData.creneau}
                    onChange={handleChange}
                    className="w-full p-3 glass border-0 rounded-xl text-white focus:ring-2 focus:ring-blue-300"
                    data-testid="select-creneau"
                  >
                    <option value="">Sélectionnez un créneau</option>
                    {contactFormData.timeSlots.map((slot) => (
                      <option key={slot} value={slot} className="bg-gray-800">
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Avez-vous déjà un conseiller financier ?
                  </label>
                  <div className="space-y-2" data-testid="radio-group-consultant">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="hasConsultant"
                        value="oui"
                        checked={formData.hasConsultant === 'oui'}
                        onChange={handleChange}
                        className="mr-3 text-blue-400"
                        data-testid="radio-consultant-oui"
                      />
                      <span className="text-gray-300">Oui</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="hasConsultant"
                        value="non"
                        checked={formData.hasConsultant === 'non'}
                        onChange={handleChange}
                        className="mr-3 text-blue-400"
                        data-testid="radio-consultant-non"
                      />
                      <span className="text-gray-300">Non</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message (optionnel) <span className="text-xs text-gray-400">({formData.message.length}/1000 caractères)</span>
                  </label>
                  <textarea
                    name="message"
                    rows="4"
                    maxLength="1000"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 resize-none transition-all ${
                      fieldErrors.message 
                        ? 'focus:ring-red-400 ring-1 ring-red-400/50' 
                        : 'focus:ring-blue-300'
                    }`}
                    placeholder="Décrivez brièvement votre situation ou vos questions..."
                    data-testid="textarea-message"
                  />
                  {fieldErrors.message && (
                    <p className="text-red-400 text-xs mt-1 flex items-center">
                      <AlertCircle size={12} className="mr-1" />
                      {fieldErrors.message}
                    </p>
                  )}
                </div>

                {/* Consentement RGPD */}
                <div className="glass p-4 rounded-xl bg-blue-500/10 border border-blue-400/30">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="consent"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                      required
                      className="mt-1 w-4 h-4 text-blue-500 border-gray-600 rounded focus:ring-blue-500"
                      data-testid="checkbox-consent"
                    />
                    <label htmlFor="consent" className="text-sm text-gray-300 leading-relaxed">
                      J'accepte que mes données personnelles soient traitées par ATOM Group SA 
                      pour répondre à ma demande de contact, conformément à la{' '}
                      <Link to="/confidentialite" className="text-blue-400 hover:text-blue-300 underline">
                        politique de confidentialité
                      </Link>.
                      <span className="text-red-400 ml-1">*</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 ml-7">
                    🔒 Vos données sont traitées en Suisse conformément au RGPD et à la LPD.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !isFormValid || !formData.consent}
                  className={`w-full py-4 text-lg font-semibold transition-all duration-300 ${
                    isLoading || !isFormValid || !formData.consent
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                      : 'btn-glass btn-primary hover:scale-[1.02]'
                  }`}
                  data-testid="btn-submit-contact"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Envoi en cours...
                    </span>
                  ) : !isFormValid ? (
                    'Veuillez compléter le formulaire'
                  ) : (
                    '🚀 Envoyer ma demande'
                  )}
                </button>
                
                {!isFormValid && Object.keys(fieldErrors).length > 0 && (
                  <div className="mt-2 text-center">
                    <p className="text-xs text-amber-400">
                      ⚠️ {Object.keys(fieldErrors).length} erreur(s) à corriger
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Informations de contact */}
            <div className="space-y-8">
              <div className="glass p-8 rounded-3xl">
                <h3 className="text-2xl font-bold heading-display text-white mb-6">
                  Nos coordonnées
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Adresse</h4>
                      <p className="text-gray-300">
                        ATOM Group SA<br />
                        c/o Microcity SA<br />
                        Rue de la Pierre-à-Mazel 39<br />
                        2000 Neuchâtel, Suisse
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone size={20} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Téléphone</h4>
                      <p className="text-gray-300">
                        <a href="tel:+41325391627" className="hover:text-white transition-colors">
                          +41 32 539 16 27
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Email</h4>
                      <p className="text-gray-300">
                        <a href="mailto:contact@atom-group.ch" className="hover:text-white transition-colors">
                          contact@atom-group.ch
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Horaires</h4>
                      <p className="text-gray-300">
                        Lundi - Vendredi : 9h00 - 17h00<br />
                        Samedi : Sur rendez-vous
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass p-8 rounded-3xl">
                <h3 className="text-xl font-bold text-white mb-4">
                  Pourquoi nous contacter ?
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <Check size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Première analyse gratuite et sans engagement
                  </li>
                  <li className="flex items-start">
                    <Check size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Conseil 100% indépendant et transparent
                  </li>
                  <li className="flex items-start">
                    <Check size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Solutions personnalisées à votre situation
                  </li>
                  <li className="flex items-start">
                    <Check size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Accompagnement sur le long terme
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;